/**
 * PATH: packages/job-hub/src/jobs/qa/qaRowMenuItems.ts
 * FILE_ID: JOBHUB-QA-ROW-MENU-ITEMS-001
 * PURPOSE: One row-menu list for the Build table and the phone cards.
 */
import type { ContextMenuItem } from '@quotech/ui'
import type { QaApi, QaItemInstance } from './qaApi'
import type { QaRowNode } from './checklistTable'
import { rowMenuClip } from './qaClipboard'
import { qaNeedsSignoff } from './qaHoldPoints'
import { qaErrorMessage } from '../tabs/job-qa/qaTabSelectors'
import { buildQaChecklistRowMenu } from './qaChecklistRowMenu'
import { buildQaChecklistRowActions, type QaChecklistEditor } from './useQaChecklistEditor'

export function qaRowMenuItems({
  editor,
  node,
  disabled,
  selectedIds,
  inspectionId,
  api,
  onMutated,
  setError,
  onUndo,
  onRedo,
}: {
  editor: QaChecklistEditor
  node: QaRowNode<QaItemInstance>
  disabled: boolean
  selectedIds: string[]
  inspectionId: string
  api: QaApi
  onMutated: () => void
  setError: (message: string | null) => void
  onUndo?: () => void
  onRedo?: () => void
}): ContextMenuItem[] {
  // Reused from: packages/job-hub/src/jobs/qa/qaChecklistRowMenu.ts (buildQaChecklistRowMenu) and packages/job-hub/src/jobs/qa/useQaChecklistEditor.ts (buildQaChecklistRowActions)
  const clip = rowMenuClip(node.item, selectedIds, inspectionId)
  async function pasteRows(position: 'above' | 'below') {
    if (!clip?.sourceIds.length) return
    try {
      await api.pasteQaNodes({
        kind: 'rows',
        source_ids: clip.sourceIds,
        target_id: node.item.checklist_instance,
        anchor_id: node.item.id,
        position,
      })
      setError(null)
      onMutated()
    } catch (error) {
      setError(qaErrorMessage(error, 'Could not paste those rows.'))
    }
  }
  return buildQaChecklistRowMenu(
    {
      isFirst: !node.hasPrevSibling,
      isLast: !node.hasNextSibling,
      hasHold: !!node.item.has_hold_point,
      needsSignoff: qaNeedsSignoff(node.item),
      disabled,
      copyCount: clip?.copyCount,
      pasteLabel: clip?.pasteLabel ?? null,
    },
    {
      ...buildQaChecklistRowActions(editor, node.item),
      onCopy: clip?.onCopy,
      onPasteAbove: () => { void pasteRows('above') },
      onPasteBelow: () => { void pasteRows('below') },
      onUndo,
      onRedo,
    },
  )
}
